import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Table, Space, message, Button, Checkbox, Popover, Select } from 'antd';
import { DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';

const { RangePicker } = DatePicker;

function SettlementSupplier() {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_price: 0 });
  const [dateRange, setDateRange] = useState(null);
  const [visibleStatuses, setVisibleStatuses] = useState(new Set());
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  useEffect(() => {
    loadSuppliers();
    loadData();
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/suppliers/all');
      if (res.code === 200) setSuppliers(res.data);
    } catch (err) {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange && dateRange[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange && dateRange[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      if (selectedSuppliers.length > 0) params.supplier_id = selectedSuppliers.join(',');

      const res = await api.get('/orders/settlement/supplier', { params });
      if (res.code === 200) {
        setRawData(res.data.list);
        setStatuses(res.data.statuses || []);
        setSummary(res.data.summary);
        // 默认显示所有状态
        if (visibleStatuses.size === 0 && res.data.statuses) {
          setVisibleStatuses(new Set(res.data.statuses.map(s => s.id)));
        }
      }
    } catch (err) {}
    setLoading(false);
  };

  // 透视数据：按 supplier+team+growth_method 分组
  const pivotData = () => {
    const groups = {};
    rawData.forEach(row => {
      const key = `${row.supplier_name || ''}|${row.team || ''}|${row.growth_method || ''}`;
      if (!groups[key]) {
        groups[key] = {
          supplier_name: row.supplier_name,
          team: row.team,
          growth_method: row.growth_method,
          statuses: {},
          totalCount: 0,
          totalPrice: 0
        };
      }
      const sid = row.status_id || 0;
      groups[key].statuses[sid] = {
        count: row.count,
        total_price: Number(row.total_price)
      };
      groups[key].totalCount += row.count;
      groups[key].totalPrice += Number(row.total_price);
    });
    return Object.values(groups);
  };

  const pivoted = pivotData();

  // 动态列
  const getColumns = () => {
    const cols = [
      { title: '主体', dataIndex: 'supplier_name', width: 120, fixed: 'left',
        onCell: (_, index) => {
          if (index === 0 || pivoted[index].supplier_name !== pivoted[index - 1].supplier_name) {
            let count = 1;
            for (let i = index + 1; i < pivoted.length && pivoted[i].supplier_name === pivoted[index].supplier_name; i++) count++;
            return { rowSpan: count };
          }
          return { rowSpan: 0 };
        }
      },
      { title: '小组', dataIndex: 'team', width: 100, fixed: 'left' },
      { title: '涨粉方式', dataIndex: 'growth_method', width: 120, fixed: 'left' }
    ];

    // 每个可见的状态：3列（数量、价格、均价）
    statuses.filter(s => visibleStatuses.has(s.id)).forEach(s => {
      cols.push({
        title: s.name,
        children: [
          { title: '数量', width: 70, align: 'right', render: (_, record) => record.statuses[s.id]?.count || 0 },
          { title: '价格', width: 100, align: 'right', render: (_, record) => `¥${(record.statuses[s.id]?.total_price || 0).toFixed(2)}` },
          { title: '均价', width: 90, align: 'right', render: (_, record) => {
            const st = record.statuses[s.id];
            if (!st || st.count === 0) return '-';
            return `¥${(st.total_price / st.count).toFixed(2)}`;
          }}
        ]
      });
    });

    // 汇总列
    cols.push({
      title: '汇总',
      children: [
        { title: '数量', width: 80, align: 'right', render: (_, record) => record.totalCount },
        { title: '价格', width: 110, align: 'right', render: (_, record) => `¥${record.totalPrice.toFixed(2)}` },
        { title: '均价', width: 90, align: 'right', render: (_, record) => record.totalCount > 0 ? `¥${(record.totalPrice / record.totalCount).toFixed(2)}` : '-' }
      ]
    });

    return cols;
  };

  // 汇总行
  const getSummaryRow = () => {
    // 计算每个状态的汇总
    const statusSummary = {};
    pivoted.forEach(row => {
      Object.entries(row.statuses).forEach(([sid, data]) => {
        if (!statusSummary[sid]) statusSummary[sid] = { count: 0, total_price: 0 };
        statusSummary[sid].count += data.count;
        statusSummary[sid].total_price += data.total_price;
      });
    });

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={3} align="right"><b>汇总</b></Table.Summary.Cell>
          {statuses.filter(s => visibleStatuses.has(s.id)).map((s, i) => {
            const ss = statusSummary[s.id] || { count: 0, total_price: 0 };
            return (
              <React.Fragment key={s.id}>
                <Table.Summary.Cell index={3 + i * 3} align="right"><b>{ss.count}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={4 + i * 3} align="right"><b>¥{ss.total_price.toFixed(2)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={5 + i * 3} align="right"><b>{ss.count > 0 ? `¥${(ss.total_price / ss.count).toFixed(2)}` : '-'}</b></Table.Summary.Cell>
              </React.Fragment>
            );
          })}
          <Table.Summary.Cell index={3 + statuses.filter(s => visibleStatuses.has(s.id)).length * 3} align="right"><b>{summary.count}</b></Table.Summary.Cell>
          <Table.Summary.Cell index={4 + statuses.filter(s => visibleStatuses.has(s.id)).length * 3} align="right"><b>¥{Number(summary.total_price).toFixed(2)}</b></Table.Summary.Cell>
          <Table.Summary.Cell index={5 + statuses.filter(s => visibleStatuses.has(s.id)).length * 3} align="right"><b>{summary.count > 0 ? `¥${(Number(summary.total_price) / summary.count).toFixed(2)}` : '-'}</b></Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const handleExport = () => {
    const headers = ['主体', '小组', '涨粉方式'];
    statuses.filter(s => visibleStatuses.has(s.id)).forEach(s => {
      headers.push(`${s.name}-数量`, `${s.name}-价格`, `${s.name}-均价`);
    });
    headers.push('汇总-数量', '汇总-价格', '汇总-均价');

    const csvRows = [headers.join(',')];
    pivoted.forEach(row => {
      const vals = [`"${row.supplier_name || ''}"`, `"${row.team || ''}"`, `"${row.growth_method || ''}"`];
      statuses.filter(s => visibleStatuses.has(s.id)).forEach(s => {
        const st = row.statuses[s.id];
        vals.push(st?.count || 0, (st?.total_price || 0).toFixed(2), st?.count > 0 ? (st.total_price / st.count).toFixed(2) : '0');
      });
      vals.push(row.totalCount, row.totalPrice.toFixed(2), row.totalCount > 0 ? (row.totalPrice / row.totalCount).toFixed(2) : '0');
      csvRows.push(vals.join(','));
    });

    const BOM = '﻿';
    const blob = new Blob([BOM + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `按主体结算_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const statusSelector = (
    <div style={{ maxWidth: 300 }}>
      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>显示的完结状态：</div>
      <Checkbox
        checked={visibleStatuses.size === statuses.length}
        indeterminate={visibleStatuses.size > 0 && visibleStatuses.size < statuses.length}
        onChange={e => {
          if (e.target.checked) {
            setVisibleStatuses(new Set(statuses.map(s => s.id)));
          } else {
            setVisibleStatuses(new Set());
          }
        }}
        style={{ marginBottom: 8 }}
      >
        全选
      </Checkbox>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {statuses.map(s => (
          <Checkbox
            key={s.id}
            checked={visibleStatuses.has(s.id)}
            onChange={e => {
              const next = new Set(visibleStatuses);
              if (e.target.checked) next.add(s.id); else next.delete(s.id);
              setVisibleStatuses(next);
            }}
          >
            {s.name}
          </Checkbox>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <h3 style={{ margin: '0 0 12px 0' }}>按主体结算汇总</h3>
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <RangePicker value={dateRange} onChange={v => setDateRange(v)} placeholder={['开始日期', '结束日期']} style={isMobile ? { width: '100%' } : {}} />
        <Select
          mode="multiple"
          placeholder="选择主体"
          style={isMobile ? { flex: 1, minWidth: 160 } : { minWidth: 200 }}
          value={selectedSuppliers}
          onChange={v => setSelectedSuppliers(v)}
          allowClear
          maxTagCount={2}
          options={suppliers.map(s => ({ label: s.name, value: s.id }))}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <Button type="primary" onClick={loadData}>查询</Button>
          <Button onClick={() => { setDateRange(null); setSelectedSuppliers([]); setTimeout(loadData, 100); }}>重置</Button>
          <Popover content={statusSelector} title={null} trigger="click" placement="bottomRight">
            <Button icon={<SettingOutlined />} />
          </Popover>
          <Button icon={<DownloadOutlined />} onClick={handleExport} />
        </div>
      </div>

      <Card>
        <Table
          rowKey={(_, i) => i}
          columns={getColumns()}
          dataSource={pivoted.map((r, i) => ({ ...r, key: i }))}
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          bordered
          size="small"
          summary={getSummaryRow}
        />
      </Card>
    </div>
  );
}

export default SettlementSupplier;
